import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { PropsWithChildren, ReactElement } from 'react'

import useScrollToElement from '~/hooks/useScrollToElement'
import { getElementBySelectorAsync } from '~/utils/dom'

const MARGIN_OFFSET = 70

interface Props {
  className?: string
  withMargin?: boolean
  withPadding?: boolean
}

const Layout = ({ children, className, withMargin, withPadding }: PropsWithChildren<Props>): ReactElement => {
  /** Local state */

  const ref = useRef<HTMLDivElement>(null)

  const [filtersHeight, setFiltersHeight] = useState(0)

  const [filtersRef, setFiltersRef] = useState<Element>()

  const [headerHeight, setHeaderHeight] = useState(0)

  const [headerRef, setHeaderRef] = useState<Element>()

  const filtersObserver = useRef(
    new ResizeObserver((entries) => setTimeout(() => setFiltersHeight(entries[0].contentRect.height), 0))
  )

  const headerObserver = useRef(
    new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height
      if (height) {
        setTimeout(() => setHeaderHeight(height), 0)
      }
    })
  )

  const computeStyle = useCallback(() => {
    if (ref.current) {
      // NOTE: lets sticky elements stay right below the fixed filters, whatever their height
      ref.current.style.setProperty('--filters-height', `${filtersHeight}px`)

      if (withPadding) {
        ref.current.style.setProperty('padding-top', `calc(${headerHeight}px + ${filtersHeight}px`)
        ref.current.style.setProperty('min-height', `calc(100vh - ${headerHeight}px - ${filtersHeight}px)`)
      }

      if (withMargin) {
        ref.current.style.setProperty('margin-top', `calc(${headerHeight}px + ${filtersHeight}px + ${MARGIN_OFFSET}px)`)
      }
    }
  }, [headerHeight, filtersHeight, ref, withMargin, withPadding])

  /** Effects */

  useEffect(() => {
    async function fetchHeaderRef() {
      const headerRef = await getElementBySelectorAsync('.header-component')

      setHeaderRef(headerRef)
    }

    fetchHeaderRef()
  }, [])

  useEffect(() => {
    async function fetchFiltersRef() {
      const filtersRef = await getElementBySelectorAsync('.filters-component')

      setFiltersRef(filtersRef)
    }

    fetchFiltersRef()
  }, [])

  useEffect(() => {
    const filtersCurrentObserver = filtersObserver.current
    const headerCurrentObserver = headerObserver.current

    filtersRef && filtersCurrentObserver.observe(filtersRef)
    headerRef && headerCurrentObserver.observe(headerRef)

    return () => {
      filtersRef && filtersCurrentObserver.unobserve(filtersRef)
      headerRef && headerCurrentObserver.unobserve(headerRef)
    }
  }, [filtersRef, headerRef])

  useEffect(() => {
    computeStyle()
  }, [computeStyle])

  useScrollToElement()

  /** Render */

  return (
    <div className={classNames('view', className)} ref={ref}>
      {children}
    </div>
  )
}

export default Layout
